/**
 * Public Document Review Page
 *
 * This page is accessible via access token without authentication.
 * External users can view documents, annotations, and participate in chat discussions.
 *
 * Route: /review/:token
 */

import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AlertCircle, CheckCircle, MessageChatSquare, File02, Download01, Clock, Lock01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { PdfViewer } from '@/components/application/pdf-viewer/pdf-viewer';
import { PolygonOverlay } from '@/components/application/pdf-viewer/polygon-overlay';
import { Avatar } from '@/components/base/avatar/avatar';
import { TextArea } from '@/components/base/textarea/textarea';
import {
    usePublicDocumentByToken,
    usePublicDocumentSuggestions,
    usePublicDocumentDownload,
    usePublicSendChatMessage,
} from '@/hooks/api/use-document-access';
import type { ReviewPolygon } from '@/types/document-review';
import { cx } from '@/utils/cx';

// Map backend suggestion to ReviewPolygon type
interface BackendSuggestion {
    document_user_suggestion_id: string;
    name: string;
    description: string | null;
    document_page: number | null;
    suggestion_type: string;
    priority: string;
    status: string;
    polygon_coordinates: {
        x: number;
        y: number;
        width: number;
        height: number;
        normalized?: boolean;
    } | null;
    chat_messages: Array<{
        chat_message_id: string;
        message_content: string;
        sender_user_id: string | null;
        sender_name: string | null;
        created_at: string;
    }>;
    created_at: string;
    updated_at: string;
}

function mapSuggestionToPolygon(suggestion: BackendSuggestion): ReviewPolygon {
    return {
        id: suggestion.document_user_suggestion_id,
        name: suggestion.name,
        pageNumber: suggestion.document_page || 1,
        x: suggestion.polygon_coordinates?.x || 0,
        y: suggestion.polygon_coordinates?.y || 0,
        width: suggestion.polygon_coordinates?.width || 0.1,
        height: suggestion.polygon_coordinates?.height || 0.05,
        notes: suggestion.description || undefined,
        status: suggestion.status as ReviewPolygon['status'],
        priority: suggestion.priority as ReviewPolygon['priority'],
        comments: suggestion.chat_messages?.map((msg) => ({
            id: msg.chat_message_id,
            userId: msg.sender_user_id || 'external',
            userName: msg.sender_name || 'Guest',
            text: msg.message_content,
            timestamp: msg.created_at,
        })),
        createdAt: suggestion.created_at,
        updatedAt: suggestion.updated_at,
    };
}

// Loading state component
function LoadingState() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-secondary">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4" />
                <p className="text-tertiary">Loading document...</p>
            </div>
        </div>
    );
}

// Error state component
function ErrorState({ title, message, showHomeLink = false }: { title: string; message: string; showHomeLink?: boolean }) {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen flex items-center justify-center bg-secondary px-4">
            <div className="max-w-md w-full bg-primary rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock01 className="size-8 text-error-600" />
                </div>
                <h1 className="text-xl font-semibold text-primary mb-2">{title}</h1>
                <p className="text-tertiary mb-6">{message}</p>
                {showHomeLink && (
                    <Button color="primary" onClick={() => navigate('/')}>
                        Go to Home
                    </Button>
                )}
            </div>
        </div>
    );
}

// All issues resolved state
function AllResolvedState({ documentName }: { documentName: string }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-secondary px-4">
            <div className="max-w-md w-full bg-primary rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="size-8 text-success-600" />
                </div>
                <h1 className="text-xl font-semibold text-primary mb-2">All Issues Resolved</h1>
                <p className="text-tertiary mb-2">
                    Great news! All issues on <strong>{documentName}</strong> have been resolved.
                </p>
                <p className="text-sm text-tertiary">
                    This review session has been completed. Thank you for your collaboration.
                </p>
            </div>
        </div>
    );
}

// Main public review page component
export function PublicDocumentReviewPage() {
    const { token } = useParams<{ token: string }>();

    // Fetch document info via public token
    const {
        data: documentInfo,
        isLoading: isLoadingDocument,
        error: documentError,
    } = usePublicDocumentByToken(token);

    // Fetch suggestions
    const {
        data: suggestionsData,
        isLoading: isLoadingSuggestions,
    } = usePublicDocumentSuggestions(token, !!documentInfo);

    // Get download URL
    const { data: downloadInfo } = usePublicDocumentDownload(token, !!documentInfo);

    // State
    const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [chatMessage, setChatMessage] = useState('');
    const [guestName, setGuestName] = useState('');

    // Send chat message mutation
    const sendChatMessage = usePublicSendChatMessage();

    // Map suggestions to polygons
    const polygons = useMemo(() => {
        if (!suggestionsData) return [];
        const suggestions = suggestionsData as BackendSuggestion[];
        return suggestions.map(mapSuggestionToPolygon);
    }, [suggestionsData]);

    // Selected polygon
    const selectedPolygon = useMemo(() => {
        return polygons.find((p) => p.id === selectedPolygonId) || null;
    }, [polygons, selectedPolygonId]);

    // Handle sending chat message
    const handleSendMessage = useCallback(async () => {
        if (!token || !selectedPolygonId || !chatMessage.trim()) return;

        try {
            await sendChatMessage.mutateAsync({
                token,
                suggestionId: selectedPolygonId,
                message: chatMessage.trim(),
                senderName: guestName.trim() || undefined,
            });
            setChatMessage('');
        } catch (error) {
            console.error('Failed to send message:', error);
        }
    }, [token, selectedPolygonId, chatMessage, guestName, sendChatMessage]);

    // Handle polygon selection
    const handleSelectPolygon = useCallback((polygonId: string) => {
        setSelectedPolygonId(polygonId);
        const polygon = polygons.find((p) => p.id === polygonId);
        if (polygon) {
            setCurrentPage(polygon.pageNumber);
        }
    }, [polygons]);

    // Loading state
    if (isLoadingDocument) {
        return <LoadingState />;
    }

    // Error state - invalid/expired token
    if (documentError) {
        const error = documentError as { status?: number };
        if (error.status === 403 || error.status === 404) {
            return (
                <ErrorState
                    title="Access Denied"
                    message="This review link is invalid or has expired. Please contact the document owner for a new link."
                    showHomeLink
                />
            );
        }
        return (
            <ErrorState
                title="Something went wrong"
                message="We couldn't load the document. Please try again later."
                showHomeLink
            />
        );
    }

    // Check if all issues are resolved
    if (documentInfo?.all_issues_resolved) {
        return <AllResolvedState documentName={documentInfo.file_name} />;
    }

    // Get file URL for viewer
    const fileUrl = downloadInfo?.download_url || '';
    const permissions = documentInfo?.permissions || { can_view: true, can_comment: false, can_suggest: false, can_resolve: false };

    // Pending issues count
    const pendingCount = polygons.filter((p) => p.status !== 'solved').length;

    return (
        <div className="min-h-screen bg-secondary">
            {/* Header */}
            <header className="bg-primary border-b border-secondary sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                            <File02 className="size-5 text-brand-600" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-primary">
                                {documentInfo?.file_name || 'Document Review'}
                            </h1>
                            <div className="flex items-center gap-2 text-sm text-tertiary">
                                {documentInfo?.document_type && (
                                    <span className="px-2 py-0.5 bg-secondary rounded text-xs">
                                        {documentInfo.document_type}
                                    </span>
                                )}
                                {pendingCount > 0 && (
                                    <span className="flex items-center gap-1 text-warning-600">
                                        <AlertCircle className="size-3" />
                                        {pendingCount} pending issue{pendingCount !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {downloadInfo && permissions.can_view && (
                            <Button
                                size="sm"
                                color="secondary"
                                iconLeading={Download01}
                                onClick={() => window.open(downloadInfo.download_url, '_blank')}
                            >
                                Download
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            {/* Main content */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
                    {/* Left: PDF Viewer */}
                    <div className="bg-primary rounded-xl border border-secondary overflow-hidden">
                        <div className="h-[calc(100vh-200px)]">
                            {fileUrl ? (
                                <div className="h-full p-4">
                                    <PdfViewer
                                        fileUrl={fileUrl}
                                        polygons={polygons}
                                        currentPage={currentPage}
                                        onPageChange={setCurrentPage}
                                        hideAddRectangle
                                        showZoomControls
                                        renderOverlay={({ pageNumber, pageWidth, pageHeight }) => (
                                            <PolygonOverlay
                                                pageNumber={pageNumber}
                                                pageWidth={pageWidth}
                                                pageHeight={pageHeight}
                                                polygons={polygons.filter((p) => p.pageNumber === pageNumber)}
                                                selectedPolygonId={selectedPolygonId}
                                                onSelectPolygon={handleSelectPolygon}
                                                readOnly
                                            />
                                        )}
                                    />
                                </div>
                            ) : isLoadingSuggestions ? (
                                <div className="h-full flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
                                </div>
                            ) : (
                                <div className="h-full flex items-center justify-center text-tertiary">
                                    Document preview not available
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Issues & Chat Panel */}
                    <div className="bg-primary rounded-xl border border-secondary overflow-hidden flex flex-col h-[calc(100vh-200px)]">
                        {/* Issues list */}
                        <div className="p-4 border-b border-secondary">
                            <h2 className="text-md font-semibold text-primary mb-3">
                                Issues ({polygons.length})
                            </h2>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                {polygons.length === 0 ? (
                                    <div className="text-center py-4 text-sm text-tertiary">
                                        No issues found
                                    </div>
                                ) : (
                                    polygons.map((polygon) => (
                                        <button
                                            key={polygon.id}
                                            onClick={() => handleSelectPolygon(polygon.id)}
                                            className={cx(
                                                'w-full p-3 rounded-lg text-left transition-colors',
                                                selectedPolygonId === polygon.id
                                                    ? 'bg-brand-100 ring-1 ring-brand-500'
                                                    : 'bg-secondary hover:bg-secondary_hover'
                                            )}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-primary truncate">
                                                        {polygon.name || `Issue #${polygon.id.substring(0, 8)}`}
                                                    </div>
                                                    <div className="text-xs text-tertiary mt-0.5">
                                                        Page {polygon.pageNumber}
                                                        {polygon.comments && polygon.comments.length > 0 && (
                                                            <span className="ml-2 inline-flex items-center gap-1">
                                                                <MessageChatSquare className="size-3" />
                                                                {polygon.comments.length}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {polygon.status === 'solved' ? (
                                                    <CheckCircle className="size-5 text-success-600 shrink-0" />
                                                ) : (
                                                    <AlertCircle className="size-5 text-warning-600 shrink-0" />
                                                )}
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Chat section */}
                        <div className="flex-1 flex flex-col min-h-0 p-4">
                            {selectedPolygon ? (
                                <>
                                    <div className="mb-3">
                                        <h3 className="text-sm font-medium text-primary">
                                            {selectedPolygon.name || `Issue #${selectedPolygon.id.substring(0, 8)}`}
                                        </h3>
                                        {selectedPolygon.notes && (
                                            <p className="text-xs text-tertiary mt-1">{selectedPolygon.notes}</p>
                                        )}
                                    </div>

                                    {/* Chat messages */}
                                    <div className="flex-1 overflow-y-auto space-y-3 mb-3">
                                        {!selectedPolygon.comments || selectedPolygon.comments.length === 0 ? (
                                            <div className="text-center py-8 text-tertiary">
                                                <MessageChatSquare className="size-8 mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">No messages yet</p>
                                                <p className="text-xs">Start the conversation</p>
                                            </div>
                                        ) : (
                                            selectedPolygon.comments.map((comment) => (
                                                <div key={comment.id} className="flex items-start gap-2">
                                                    <Avatar size="sm" />
                                                    <div className="flex-1">
                                                        <div className="flex items-baseline gap-2 mb-0.5">
                                                            <span className="text-sm font-medium text-primary">
                                                                {comment.userName}
                                                            </span>
                                                            <span className="text-xs text-tertiary">
                                                                {new Date(comment.timestamp).toLocaleString('it-IT', {
                                                                    day: 'numeric',
                                                                    month: 'short',
                                                                    hour: 'numeric',
                                                                    minute: '2-digit',
                                                                })}
                                                            </span>
                                                        </div>
                                                        <div className="p-2 bg-secondary rounded-lg text-sm text-primary">
                                                            {comment.text}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {/* Chat input */}
                                    {permissions.can_comment && (
                                        <div className="space-y-2">
                                            {/* Guest name input (if not set) */}
                                            {!guestName && (
                                                <input
                                                    type="text"
                                                    placeholder="Your name (optional)"
                                                    value={guestName}
                                                    onChange={(e) => setGuestName(e.target.value)}
                                                    className="w-full px-3 py-2 text-sm border border-secondary rounded-lg bg-primary text-primary placeholder-tertiary"
                                                />
                                            )}
                                            <div className="flex gap-2">
                                                <TextArea
                                                    value={chatMessage}
                                                    onChange={(val) => setChatMessage(val as string)}
                                                    placeholder="Write a message..."
                                                    rows={2}
                                                    textAreaClassName={() => 'resize-none'}
                                                    className="flex-1"
                                                />
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    onClick={handleSendMessage}
                                                    disabled={!chatMessage.trim() || sendChatMessage.isPending}
                                                    isLoading={sendChatMessage.isPending}
                                                    className="self-end"
                                                >
                                                    Send
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {!permissions.can_comment && (
                                        <div className="text-center py-4 text-sm text-tertiary bg-secondary rounded-lg">
                                            <Lock01 className="size-4 mx-auto mb-1" />
                                            You don't have permission to comment
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-tertiary">
                                    <div className="text-center">
                                        <MessageChatSquare className="size-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">Select an issue to view the discussion</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="fixed bottom-0 left-0 right-0 bg-primary border-t border-secondary py-2 px-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-tertiary">
                    <div className="flex items-center gap-1">
                        <Clock className="size-3" />
                        <span>Review access provided by EveMind</span>
                    </div>
                    <div>
                        <a href="https://evemind.ai" target="_blank" rel="noopener noreferrer" className="hover:text-primary">
                            evemind.ai
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default PublicDocumentReviewPage;
